/**
 * The tabs of an employee profile a reader is offered, and which tab opens
 * when the URL names one they may not.
 *
 * Every tab but Job and Documents reads data behind a permission of its own.
 * The tab bar (`buildProfileTabs`) and the `?tab=` fallback on the profile page
 * (`resolveProfileTab`) both ask `canOpenProfileTab`, so they agree on every
 * one of them — a tab hidden from the bar but mounted from a bookmarked link
 * renders a 403 as an empty list, and a tab offered by the bar but forced to
 * Job is a button that does nothing. [HW-940] Notes is decided by
 * `canViewEmployeeNotes` [HW-1053]. Absences, Emergency and Compensations
 * used to be decided only for the bar, inside `ProfileCard`, so `?tab=absences`
 * still mounted them; `profileTabAccess` now decides them with the rest. [HW-942]
 *
 * Documents opens for everyone who can open the card: it holds the FOP payment
 * details and Education blocks, and each block — like the file tree, which keeps
 * the `canViewEmployeeDocuments` gate (HW-785) — hides itself from a reader
 * without its own permission. [HW-1008]
 *
 * Kept out of the components because the repo's vitest runs in a node
 * environment, where a component cannot be rendered — same reasoning as
 * `features/time-tracker/lib/time-tabs.ts`.
 */

import type { AuthenticatedUser } from '@hygge/shared';
import {
  canReadAnyEmployeeAbsences,
  canViewEmployeeDocuments,
  canViewEmployeeNotes,
  hasPermission,
  hasUnscopedPermission,
} from '@/lib/permissions';

/** Which of the gated tabs this reader may open on this employee's profile. */
export interface ProfileTabAccess {
  /** `employees.emergency.view` (unscoped), or the reader's own profile. */
  canViewEmergency: boolean;
  /**
   * `canViewEmployeeDocuments(user, employeeId)` — own, team or global documents read.
   * No longer gates the Documents tab, only its file tree and document-count badge. [HW-1008]
   */
  canViewDocuments: boolean;
  /** `assets.view` — what `GET /assets/employee/:employeeId` requires. */
  canViewAssets: boolean;
  /** `access.view` — what `GET /access-grants/employee/:employeeId` requires. */
  canViewAccess: boolean;
  /** `canViewEmployeeNotes(user, employeeId)` — what `GET /employees/:id/notes` admits. [HW-1053] */
  canViewNotes: boolean;
  /**
   * `readsAnyEmployeeAbsences`, or `employees.absences.view.self` on the reader's
   * own profile — what `AbsenceReadAccessService` admits.
   */
  canViewAbsences: boolean;
  /**
   * `employees.salary.view`, scoped forms included — what `PermissionsGuard`
   * admits on `GET /employees/:id/compensations`.
   */
  canViewCompensations: boolean;
  /** `employees.audit.view` — what `GET /employees/:id/audit-log` requires. [HW-795] */
  canViewHistory: boolean;
}

/** The permissions behind `ProfileTabAccess`, as `profileTabPermissions` reads them. */
export interface ProfileTabPermissions {
  /** The profile is the reader's own. */
  isSelf: boolean;
  /** `canViewEmployeeDocuments(user, employeeId)`. */
  documents: boolean;
  /**
   * Unscoped, as `EmployeesService.findOne` reads it: the code or a wildcard,
   * never `employees.emergency.view.self`. [HW-942]
   */
  'employees.emergency.view': boolean;
  'assets.view': boolean;
  'access.view': boolean;
  /** `canViewEmployeeNotes(user, employeeId)` — already applies the `.self` rule. [HW-1053] */
  notes: boolean;
  /**
   * `canReadAnyEmployeeAbsences(user)` — `absences.requests.view`,
   * `absences.requests.manage` or `absences.balances.view`, unscoped. [HW-942]
   */
  readsAnyEmployeeAbsences: boolean;
  'employees.absences.view.self': boolean;
  'employees.salary.view': boolean;
  'employees.audit.view': boolean;
}

/**
 * One answer for the tab bar and the `?tab=` fallback. The `.self` inputs are
 * applied only on the reader's own profile, and Emergency is always open there
 * — its contact details are the reader's own. The `.self` rule decides Absences
 * here (`employees.absences.view.self` is none of the any-employee codes);
 * Notes arrives already decided by `canViewEmployeeNotes`. [HW-1053]
 */
export function profileTabAccess(p: ProfileTabPermissions): ProfileTabAccess {
  return {
    canViewEmergency: p['employees.emergency.view'] || p.isSelf,
    canViewDocuments: p.documents,
    canViewAssets: p['assets.view'],
    canViewAccess: p['access.view'],
    canViewNotes: p.notes,
    canViewAbsences: p.readsAnyEmployeeAbsences || (p.isSelf && p['employees.absences.view.self']),
    canViewCompensations: p['employees.salary.view'],
    canViewHistory: p['employees.audit.view'],
  };
}

/**
 * What `profileTabAccess` is given on the profile page: each code read the way
 * the backend reads it for that tab. Most tabs sit behind `PermissionsGuard`,
 * which lets a scoped variant stand in for the base code (`hasPermission`);
 * Emergency, Absences, Notes and Documents are decided inside their services,
 * each by its own rule. A pure function, so the choice of rule per tab is
 * tested and not left to the component. [HW-942]
 */
export function profileTabPermissions(
  user: AuthenticatedUser | null,
  employeeId: string | null | undefined
): ProfileTabPermissions {
  const ownId = user?.employee?.id;
  return {
    isSelf: !!employeeId && !!ownId && ownId === employeeId,
    documents: canViewEmployeeDocuments(user, employeeId),
    'employees.emergency.view': hasUnscopedPermission(user, 'employees.emergency.view'),
    'assets.view': hasPermission(user, 'assets.view'),
    'access.view': hasPermission(user, 'access.view'),
    notes: canViewEmployeeNotes(user, employeeId),
    readsAnyEmployeeAbsences: canReadAnyEmployeeAbsences(user),
    'employees.absences.view.self': hasPermission(user, 'employees.absences.view.self'),
    'employees.salary.view': hasPermission(user, 'employees.salary.view'),
    'employees.audit.view': hasPermission(user, 'employees.audit.view'),
  };
}

/** Every tab of the profile, in the order the bar shows them. */
export const PROFILE_TAB_IDS = [
  'job',
  'emergency',
  'documents',
  'assets',
  'access',
  'notes',
  'absences',
  'compensations',
  'history',
] as const;

export type ProfileTabId = (typeof PROFILE_TAB_IDS)[number];

/** Whether a URL's `?tab=` names a tab at all — spelling only; `resolveProfileTab` decides access. */
export function isProfileTabId(value: unknown): value is ProfileTabId {
  return typeof value === 'string' && (PROFILE_TAB_IDS as readonly string[]).includes(value);
}

/** The tabs `canOpenProfileTab` decides — every tab but Job and Documents. */
export type GatedProfileTab = Exclude<ProfileTabId, 'job' | 'documents'>;

const GATES: Record<GatedProfileTab, keyof ProfileTabAccess> = {
  emergency: 'canViewEmergency',
  assets: 'canViewAssets',
  access: 'canViewAccess',
  notes: 'canViewNotes',
  absences: 'canViewAbsences',
  compensations: 'canViewCompensations',
  history: 'canViewHistory',
};

/** Whether the reader may open `tab`. Job, Documents, and names that are no tab, pass. */
export function canOpenProfileTab(tab: string, access: ProfileTabAccess): boolean {
  return Object.hasOwn(GATES, tab) ? access[GATES[tab as GatedProfileTab]] : true;
}

/**
 * The tab that actually opens: the one asked for, or Job when the reader may
 * not open it. `?tab=` mounts a tab straight from the URL, past the tab bar, so
 * a link pasted between colleagues whose roles differ must not land on a tab
 * whose data the reader is refused.
 */
export function resolveProfileTab<T extends string>(
  requested: T,
  access: ProfileTabAccess
): T | 'job' {
  return canOpenProfileTab(requested, access) ? requested : 'job';
}

export interface ProfileTab {
  id: ProfileTabId;
  label: string;
  badge?: number;
}

/** What the bar needs beyond `ProfileTabAccess`: the badge counts. */
export interface ProfileTabOptions {
  access: ProfileTabAccess;
  documentCount?: number;
  assetCount?: number;
  accessCount?: number;
}

const LABELS: Record<ProfileTabId, string> = {
  job: 'Job',
  emergency: 'Emergency',
  documents: 'Documents',
  assets: 'Assets',
  access: 'Access',
  notes: 'Notes',
  absences: 'Absences',
  compensations: 'Compensations',
  history: 'History',
};

const badge = (count: number | undefined) => (count && count > 0 ? count : undefined);

/** The tabs this reader is offered, in the order the bar shows them. */
export function buildProfileTabs(opts: ProfileTabOptions): ProfileTab[] {
  const counts: Partial<Record<ProfileTabId, number | undefined>> = {
    documents: opts.documentCount,
    assets: opts.assetCount,
    access: opts.accessCount,
  };
  return PROFILE_TAB_IDS.filter((id) => canOpenProfileTab(id, opts.access)).map((id) => {
    const tab: ProfileTab = { id, label: LABELS[id] };
    const count = badge(counts[id]);
    if (count !== undefined) tab.badge = count;
    return tab;
  });
}
